import React, { FC, useState, useEffect, MouseEvent, useMemo } from "react";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { Button, View } from "@tarojs/components";

import "./index.scss";
import { XHComponentCommonProps } from "../../types";
import { replaceCharByIndex } from "../../utils";

export type NumberKeyboardType = "default" | "custom";

export type NumberKeyboardProps = {
  show: boolean;
  showClose?: boolean;
  closeOnClickSpace?: boolean;
  maxLength?: number;
  onBlur?: (e: MouseEvent | Event) => void;
  onClose?: (e: MouseEvent | Event) => void;
  onChange?: (value: string) => void;
  onDelete: () => void;
  value: string;
  closeText?: string;
  activeIndex: number;
  dataTrackId?: string;
} & XHComponentCommonProps;

export type KeyType = "number" | "delete" | "string" | "none";

export interface IKey {
  text: number | string;
  value: number | string;
  type: KeyType;
  activeIndex: number;
}

const LICENSEPLATE_PROVINCES = [
  "京",
  "津",
  "冀",
  "晋",
  "蒙",
  "辽",
  "吉",
  "黑",
  "沪",
  "苏",
  "浙",
  "皖",
  "闽",
  "赣",
  "鲁",
  "豫",
  "鄂",
  "湘",
  "粤",
  "桂",
  "琼",
  "渝",
  "川",
  "贵",
  "云",
  "藏",
  "陕",
  "甘",
  "青",
  "宁",
  "新",
  "港",
  "澳",
  "台",
  "使",
];

const LICENSEPLATE_NUMBERS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

const LICENSEPLATE_LETTERS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "J",
  "K",
  "L",
  "M",
  "N",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

const specialArea = ["港", "澳", "学", "领"];
const finalArea = ["临", "澳", "学", "领"];

export type TYPE_MODE = "province" | "letter" | "mixed";

const LicensePlateKeyboard: FC<NumberKeyboardProps> = props => {
  const {
    onBlur,
    show,
    showClose = true,
    closeOnClickSpace = true,
    onClose,
    onChange,
    value,
    closeText = "完成",
    onDelete,
    className,
    activeIndex,
    dataTrackId = "LicensePlate_Keyboard",
  } = props;
  const [input, setInput] = useState(value);

  // 键盘的key
  const keys = useMemo(() => {
    if (activeIndex === 0) return LICENSEPLATE_PROVINCES;
    if (activeIndex === 6)
      return [...LICENSEPLATE_NUMBERS, ...LICENSEPLATE_LETTERS, ...specialArea];
    if (activeIndex === 7)
      return [...LICENSEPLATE_NUMBERS, ...LICENSEPLATE_LETTERS, ...finalArea];
    return [...LICENSEPLATE_NUMBERS, ...LICENSEPLATE_LETTERS];
  }, [activeIndex]);

  const title = useMemo(() => {
    if (activeIndex === 0) return "选择省份";
    if (activeIndex === 1) return "选择字母";
    return "选择数字或字母";
  }, [activeIndex]);

  const mode = useMemo<TYPE_MODE>(() => {
    if (activeIndex === 0) return "province";
    if (activeIndex === 1) return "letter";
    return "mixed";
  }, [activeIndex]);

  useEffect(() => {
    if (closeOnClickSpace) {
      document.addEventListener("click", handleClose);
    }

    return () => {
      document.removeEventListener("click", handleClose);
    };
  }, []);

  useEffect(() => {
    setInput(value);
  }, [value]);

  function handleClose(e): void {
    e.stopPropagation();
    onClose && onClose(e);
    onBlur && onBlur(e);
  }

  /**
   * 根据 activeIndex 拼凑新值，如果activeIndex小于当前的值的长度，就说明后面已经有值，这时候直接替换，否则拼接
   * @param key
   */
  function handleKeyPress(key: string): void {
    if (key === value[activeIndex]) return;
    if (process.env.TARO_ENV === "h5") {
      // e.stopImmediatePropagation();
    }
    let newInput = input;
    if (activeIndex < input.length) {
      newInput = replaceCharByIndex(input, activeIndex, key);
    } else {
      newInput = input + key;
    }

    setInput(newInput);

    onChange && onChange(newInput);
  }

  const handleDelete = () => {
    onDelete();
  };

  const keyboardcls = classNames("xh-license-keyboard", className);

  return (
    <CSSTransition classNames="slide-up" in={show} unmountOnExit timeout={200}>
      <View className={keyboardcls}>
        <View className="xh-license-keyboard-header">
          <View className="xh-license-keyboard-header-title">{title}</View>
          <View className="xh-license-keyboard-header-delete"></View>
          {showClose && (
            <View
              className="xh-license-keyboard-header-close"
              // @ts-ignore
              dataTrackId={`${dataTrackId}_dataTrackId_close`}
              onClick={handleClose}
            >
              {closeText}
            </View>
          )}
        </View>
        <View className="xh-license-keyboard-body">
          {keys.map((key, index) => (
            <View
              key={index}
              className={classNames("xh-license-keyboard-body-key")}
            >
              {process.env.TARO_ENV === "h5" ? (
                <button
                  className={classNames(
                    "xh-license-keyboard-body-key-item",
                    process.env.TARO_ENV
                  )}
                  // @ts-ignore
                  dataTrackId={`${dataTrackId}_Keyboard_${key}`}
                  disabled={mode === "letter" && index < 10}
                  onClick={e => {
                    e.stopPropagation();
                    handleKeyPress(key);
                  }}
                >
                  {key}
                </button>
              ) : (
                <Button
                  className={classNames(
                    "xh-license-keyboard-body-key-item",
                    process.env.TARO_ENV
                  )}
                  disabled={mode === "letter" && index < 10}
                  // @ts-ignore
                  dataTrackId={`${dataTrackId}_Keyboard_${key}`}
                  onClick={e => {
                    e.stopPropagation();
                    if (mode === "letter" && index < 10) return;
                    handleKeyPress(key);
                  }}
                >
                  {key}
                </Button>
              )}
            </View>
          ))}
          <View
            className={classNames("xh-license-keyboard-body-key", "delete")}
          >
            {process.env.TARO_ENV === "h5" ? (
              <button
                className={classNames(
                  "xh-license-keyboard-body-key-item",
                  process.env.TARO_ENV,
                  "delete"
                )}
                disabled={activeIndex === 0}
                // @ts-ignore
                dataTrackId={`${dataTrackId}_Keyboard_delete`}
                onClick={e => {
                  e.stopPropagation();
                  handleDelete();
                }}
              ></button>
            ) : (
              <Button
                className={classNames(
                  "xh-license-keyboard-body-key-item",
                  process.env.TARO_ENV,
                  "delete"
                )}
                disabled={activeIndex === 0}
                // @ts-ignore
                dataTrackId={`${dataTrackId}_Keyboard_delete`}
                onClick={e => {
                  e.stopPropagation();
                  if (value.length === 0) return;
                  console.log("delete");
                  handleDelete();
                }}
              ></Button>
            )}
          </View>
        </View>
      </View>
    </CSSTransition>
  );
};

export default LicensePlateKeyboard;
